import { AuthContext } from '@repo-level/core-front';
import { isSafeRedirect } from 'modules/common/helpers/isSafeRedirect';
import { sendLoginFormSubmitEvent } from 'modules/googleAnalytics/events/authEvents';
import { useRiskSegmentSync } from 'modules/riskSegment/hooks/useRiskSegmentSync';
import { useContext, useEffect } from 'react';
import { useNavigation } from 'react-navi';
import { captureAppSource } from '../helpers/appSource';
import { isNeedAuthDataConfirmation } from '../helpers/authFields';
import {
  googleAuthRedirectStorage,
  pendingGoogleAuthStorage,
} from '../helpers/pendingGoogleAuth';

export const useGoogleAuthReturn = () => {
  const { user, pendingUser } = useContext(AuthContext);

  const triggerRiskSegmentSync = useRiskSegmentSync();
  const { navigate } = useNavigation();

  // Mounted with the shell, so this runs before a navigation can drop the
  // marker the app puts on the entry url.
  useEffect(captureAppSource, []);

  // A bfcache restore means we came back without a load, so the attempt was
  // abandoned and its flag must not outlive it.
  useEffect(() => {
    const dropAbandoned = ({ persisted }: PageTransitionEvent) => {
      if (!persisted) return;

      pendingGoogleAuthStorage.remove();
      googleAuthRedirectStorage.remove();
    };

    window.addEventListener('pageshow', dropAbandoned);
    return () => window.removeEventListener('pageshow', dropAbandoned);
  }, []);

  useEffect(() => {
    if (pendingUser) return;
    if (!pendingGoogleAuthStorage.get()) return;

    pendingGoogleAuthStorage.remove();

    // Whatever the outcome, the target must not outlive this attempt.
    const storedRedirect = googleAuthRedirectStorage.get();
    googleAuthRedirectStorage.remove();

    // An abandoned or failed provider flow comes back without a session.
    if (!user?.id) return;

    // A player still missing their data has just registered, and the
    // confirmation form reports that result instead.
    if (isNeedAuthDataConfirmation(user)) return;

    triggerRiskSegmentSync(user);

    sendLoginFormSubmitEvent({ status: 'success', method: 'google', user });

    // Only a returning player is sent on: a fresh account still owes the
    // confirmation, welcome and deposit steps.
    if (isSafeRedirect(storedRedirect)) navigate(storedRedirect);
  }, [navigate, pendingUser, triggerRiskSegmentSync, user]);
};
